/**
 * 评估器参数卡片的图表区：按 spec.charts 声明，把 summary 里的计数键画成环形占比 / 分类柱状。
 *
 * 只读 summary（后端已 ∑ 好），不另取数。
 * - 单 Job：传 summary；
 * - 两 Job 对比：再传 baseSummary，同一份声明各画一份——donut 并排两个环（当前 / 对比，
 *   各按自己的分母算占比），bar 改分组双柱（同一类目下当前 / 对比两根）。
 *
 * donut 占比分母由 percentBase 决定："sum" = 各项合计（恒 100%）；"key:<k>" = 占某计数键，
 * 此时各项之和可能 < 或 > 该键，不足部分补灰色「其余」，超出时不补（口径不互斥，提示别按占比读）。
 */
import { useMemo } from 'react';
import { Empty, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';

import type { EvaluatorMetricAggChart } from '@/apis/simulation';

// 与页面既有 pass=绿 / fail=橙 / error=红 同族，分类色循环取用
const PALETTE = ['#16a34a', '#eab308', '#3b82f6', '#f97316', '#8b5cf6', '#06b6d4', '#dc2626', '#84cc16'];
const REST_COLOR = '#d4d4d8';
// 对比柱：当前=蓝，对比=灰，不跟类目色走（类目已在横轴上区分）
const CURR_COLOR = '#3b82f6';
const BASE_COLOR = '#a1a1aa';

/** summary 里本组件需要的最小字段（单 Job / 对比两种 summary 都能适配成这个形状） */
export interface ChartSummaryItem {
  key: string;
  label: string;
  value: number | null;
}

interface Props {
  charts: EvaluatorMetricAggChart[];
  summary: ChartSummaryItem[];
  /** 传入则进入对比模式 */
  baseSummary?: ChartSummaryItem[];
}

const fmt = (v: number) => v.toLocaleString('zh-CN', { maximumFractionDigits: 3 });

/** 「失败原因分布·路径不可行」→「路径不可行」：同组键 label 常带「分组名·」前缀，图例只留末段 */
const shortLabel = (label: string) => {
  const i = label.lastIndexOf('·');
  return i >= 0 ? label.slice(i + 1) : label;
};

interface Slice { key: string; name: string; full: string; value: number | null }

/** 按 seriesKeys 取出一侧的数据点 + 占比分母。
 *
 * ``value === null``（该键有样本空间但分母为 0，如率键 ∑den==0）**保留 null 语义**，
 * 与「键缺席」区分开：null 不画扇区 / 不画柱（ECharts data 传 null），但类目仍参与
 * 对齐；键根本没出现在该侧 summary 里才叫缺失，由调用方按 0 补位。
 */
function pickSide(chart: EvaluatorMetricAggChart, summary: ChartSummaryItem[]) {
  const byKey = new Map(summary.map((m) => [m.key, m]));
  const items: Slice[] = chart.seriesKeys
    .map((k) => byKey.get(k))
    .filter((m): m is ChartSummaryItem => !!m)
    .map((m) => ({ key: m.key, name: shortLabel(m.label), full: m.label, value: m.value }));
  const total = items.reduce((a, b) => a + (b.value ?? 0), 0);
  let base = total;
  let baseLabel = '各项合计';
  if (chart.percentBase.startsWith('key:')) {
    const bk = byKey.get(chart.percentBase.slice(4));
    if (bk && bk.value !== null) {
      base = bk.value;
      baseLabel = shortLabel(bk.label);
    }
  }
  return { items, total, base, baseLabel, overBase: total > base };
}

type Side = ReturnType<typeof pickSide>;

function pieSeries(side: Side, center: [string, string], name: string, radius: [string, string]) {
  const pctOf = (v: number) => (side.base > 0 ? (v / side.base) * 100 : 0);
  // 值为 0 的类目不画扇区（minAngle 会把 0 也撑成一条细线，和小扇区的标签挤在一起）；
  // null（无样本，分母为 0）同样不画扇区。颜色仍按其在 series 里的原始序号取，图例颜色不错位。
  const data = side.items
    .map((i, idx) => ({
      name: i.name, full: i.full, value: i.value, itemStyle: { color: PALETTE[idx % PALETTE.length] },
    }))
    .filter((d): d is typeof d & { value: number } => d.value !== null && d.value > 0);
  if (side.base > side.total) {
    data.push({ name: '其余', full: '其余（分母减去各项之和）', value: side.base - side.total, itemStyle: { color: REST_COLOR } });
  }
  return {
    name,
    type: 'pie' as const,
    radius,
    center,
    padAngle: 2,
    minAngle: 4,
    itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 },
    // 标签放到环外、两行（数值 / 占比），比单行「25 · 60%」窄一半，不会被卡片边缘截断；
    // overflow none 不再用省略号截；moveOverlap 让相邻小扇区的标签上下错开，不压在环上。
    label: {
      show: true,
      position: 'outside' as const,
      alignTo: 'none' as const,
      distanceToLabelLine: 3,
      overflow: 'none' as const,
      fontSize: 11,
      lineHeight: 15,
      color: '#4b5563',
      formatter: (p: any) => (p.value === 0 ? '' : `${fmt(p.value)}\n${pctOf(p.value).toFixed(1)}%`),
    },
    labelLine: { show: true, length: 10, length2: 8, smooth: 0.2 },
    labelLayout: { hideOverlap: false, moveOverlap: 'shiftY' as const },
    // 鼠标移到扇区或它的文字标签上，都高亮该扇区并弹出详情 tooltip
    emphasis: { scaleSize: 6, label: { fontSize: 12, fontWeight: 600 } },
    data,
  };
}

function ChartPanel({ chart, summary, baseSummary }: { chart: EvaluatorMetricAggChart } & Pick<Props, 'summary' | 'baseSummary'>) {
  const compare = baseSummary !== undefined;
  const curr = useMemo(() => pickSide(chart, summary), [chart, summary]);
  const base = useMemo(() => (compare ? pickSide(chart, baseSummary!) : null), [chart, baseSummary, compare]);

  const option: EChartsOption = useMemo(() => {
    if (chart.type === 'bar') {
      // 类目 = seriesKeys 并集（两侧各自缺失的键按 0 补位，类目对齐才能分组）
      const keys = chart.seriesKeys.filter(
        (k) => curr.items.some((i) => i.key === k) || base?.items.some((i) => i.key === k),
      );
      const nameOf = (k: string) => (curr.items.find((i) => i.key === k) ?? base?.items.find((i) => i.key === k))?.name ?? k;
      // 该侧**完全没有**这个键 → 0（缺失补位，类目对齐）；有键但值为 null（分母为 0）
      // → null 原样传入 ECharts：不画柱、不下标签，tooltip 显示「—」，不拿 0 顶替「无样本」。
      const valOf = (side: Side | null, k: string): number | null => {
        const it = side?.items.find((i) => i.key === k);
        return it ? it.value : 0;
      };
      // null 值（无样本）不画柱时标签也不下数字；ECharts 对 null 点默认 tooltip 显示 «—»。
      const barLabel = { show: true, position: 'top' as const, fontSize: 11, formatter: (p: any) => (p.value == null ? '' : fmt(p.value)) };
      const series = compare
        ? [
            { name: '当前任务', type: 'bar' as const, barMaxWidth: 28, itemStyle: { color: CURR_COLOR }, label: barLabel, data: keys.map((k) => valOf(curr, k)) },
            { name: '对比任务', type: 'bar' as const, barMaxWidth: 28, itemStyle: { color: BASE_COLOR }, label: barLabel, data: keys.map((k) => valOf(base, k)) },
          ]
        : [{
            type: 'bar' as const,
            barMaxWidth: 36,
            data: keys.map((k, idx) => ({ value: valOf(curr, k), itemStyle: { color: PALETTE[idx % PALETTE.length] } })),
            label: barLabel,
          }];
      return {
        grid: { left: 48, right: 16, top: compare ? 36 : 24, bottom: 56 },
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, confine: true },
        ...(compare ? { legend: { top: 0, textStyle: { fontSize: 12, color: '#4b5563' } } } : {}),
        xAxis: {
          type: 'category',
          data: keys.map(nameOf),
          axisLabel: { fontSize: 11, color: '#4b5563', interval: 0, rotate: keys.length > 5 ? 30 : 0 },
        },
        yAxis: { type: 'value', axisLabel: { fontSize: 11, color: '#4b5563' } },
        series,
      };
    }
    // donut：单 Job 一个环 + 底部图例；对比 = 左右两个环 + 顶部标题 + 底部共享图例
    const tooltip = {
      trigger: 'item' as const,
      confine: true,
      extraCssText: 'max-width:320px;white-space:normal;line-height:1.7;',
      formatter: (p: any) => {
        const side = compare && p.seriesName === '对比任务' ? base! : curr;
        const pct = side.base > 0 ? ((p.value / side.base) * 100).toFixed(2) : '—';
        const full = p.data?.full ?? p.name;
        return (
          (compare ? `<div style="color:#9b9da3;font-size:11px">${p.seriesName}</div>` : '') +
          `<div><b>${full}</b></div>` +
          `<div>数值：${fmt(p.value)}</div>` +
          `<div>占比：${pct}%（分母 ${side.baseLabel} ${fmt(side.base)}）</div>`
        );
      },
    };
    // 图例按 seriesKeys 全量列出（含值为 0 的类目），颜色与扇区一致；0 值类目在 tooltip 里不可悬停，
    // 想知道它的数值看下方指标卡。
    const legend = { bottom: 0, icon: 'circle', itemGap: 12, textStyle: { fontSize: 12, color: '#4b5563' } };
    if (!compare) {
      return {
        tooltip,
        legend,
        series: [pieSeries(curr, ['50%', '44%'], '当前任务', ['36%', '56%'])],
      };
    }
    return {
      tooltip,
      legend,
      title: [
        { text: '当前任务', left: '25%', top: 0, textAlign: 'center', textStyle: { fontSize: 12, fontWeight: 500, color: '#4b5563' } },
        { text: '对比任务', left: '75%', top: 0, textAlign: 'center', textStyle: { fontSize: 12, fontWeight: 500, color: '#4b5563' } },
      ],
      series: [
        pieSeries(curr, ['25%', '48%'], '当前任务', ['32%', '50%']),
        pieSeries(base!, ['75%', '48%'], '对比任务', ['32%', '50%']),
      ],
    };
  }, [chart, curr, base, compare]);

  const sideEmpty = (s: Side | null) =>
    s === null || s.items.length === 0 ||
    (chart.type === 'donut'
      ? s.base <= 0
      : s.items.every((i) => i.value === null || i.value === 0));
  const empty = sideEmpty(curr) && sideEmpty(base);
  // 任一侧占比之和超分母都要提示
  const overBase = curr.overBase || !!base?.overBase;

  return (
    <div style={{ flex: compare ? '1 1 520px' : '1 1 360px', minWidth: 320, padding: 12, border: '1px solid #f0f0f0', borderRadius: 6, background: '#fff' }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2329', marginBottom: 4 }}>
        {chart.title}
        {chart.type === 'donut' && (
          <Tooltip
            title={
              <div style={{ fontSize: 12, lineHeight: 1.7, maxWidth: 300 }}>
                <div>
                  占比分母：{curr.baseLabel}
                  {curr.base > 0 ? `（当前 ${fmt(curr.base)}${base ? `，对比 ${fmt(base.base)}` : ''}）` : ''}。
                </div>
                {compare && <div>两个环各按自己的分母算占比，对比的是构成，不是绝对量。</div>}
                {overBase && <div>各项之和大于分母，说明这组计数不互斥，占比之和会超过 100%，请勿按占比读。</div>}
              </div>
            }
          >
            <InfoCircleOutlined style={{ marginLeft: 6, fontSize: 11, color: overBase ? '#f97316' : '#c4c6cc' }} />
          </Tooltip>
        )}
      </div>
      {empty ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="无数据" style={{ margin: '24px 0' }} />
      ) : (
        <ReactECharts option={option} style={{ height: 300 }} notMerge />
      )}
    </div>
  );
}

export default function MetricAggCharts({ charts, summary, baseSummary }: Props) {
  if (charts.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
      {charts.map((c) => (
        <ChartPanel key={c.id} chart={c} summary={summary} baseSummary={baseSummary} />
      ))}
    </div>
  );
}
